import { useState } from "react";
import {
  Button,
  Group,
  Stack,
  Text,
  FileButton,
  ActionIcon,
  Box,
} from "@mantine/core";
import { IconUpload, IconTrash, IconPhoto } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { uploadsApi, type UploadType } from "../../api/uploads";
import { errorMessage } from "../../lib/axios";
import { ZoomableImage } from "./ZoomableImage";

// Real file upload (→ S3 via backend) replacing hand-typed image URLs.
// `value` is the stored S3 URL/key; `onChange` receives the new stored URL.
export function ImageUploadField({
  label,
  value,
  onChange,
  folder = "admin",
  uploadType = "image",
  required,
  previewHeight = 120,
}: {
  label?: string;
  value?: string | null;
  onChange: (url: string | null) => void;
  folder?: string;
  uploadType?: UploadType;
  required?: boolean;
  previewHeight?: number;
}) {
  const [loading, setLoading] = useState(false);

  async function handleSelect(file: File | null) {
    if (!file) return;
    setLoading(true);
    try {
      const result = await uploadsApi.uploadFile(file, { uploadType, folder });
      onChange(result.location);
    } catch (err) {
      notifications.show({
        color: "red",
        title: "Upload failed",
        message: errorMessage(err),
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <Stack gap={6}>
      {label && (
        <Text size="sm" fw={500}>
          {label}
          {required && (
            <Text component="span" c="red">
              {" "}
              *
            </Text>
          )}
        </Text>
      )}

      <Group align="flex-start" gap="md">
        <Box w={previewHeight * 1.4}>
          <ZoomableImage src={value} h={previewHeight} fit="contain" emptyLabel="No image" title={label} />
        </Box>

        <Stack gap="xs">
          <FileButton onChange={handleSelect} accept="image/png,image/jpeg,image/webp,image/gif">
            {(props) => (
              <Button
                {...props}
                variant="light"
                leftSection={value ? <IconPhoto size={16} /> : <IconUpload size={16} />}
                loading={loading}
              >
                {value ? "Replace image" : "Upload image"}
              </Button>
            )}
          </FileButton>

          {value && (
            <ActionIcon
              variant="subtle"
              color="red"
              onClick={() => onChange(null)}
              aria-label="Remove image"
            >
              <IconTrash size={16} />
            </ActionIcon>
          )}

          <Text size="xs" c="dimmed">
            PNG/JPG/WebP, up to 5 MB.
          </Text>
        </Stack>
      </Group>
    </Stack>
  );
}
